import { styled } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ErrorIcon from "@locaisolutions/icons/dist/icons24px/Error24Px";

import { TemporaryConfigurationMenuStatusType } from "./TemporaryNewConfigurationData";
import { mainColours } from "../../lib/colors";

const MainContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  background: theme.palette.background.default,
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  padding: theme.spacing(1),
  paddingRight: 0,
  width: "-webkit-fill-available"
}));

type TemporaryNewSiteConfigurationsProps = {
  siteConfiguration: NewSiteConfiguration;
  temporaryConfigurationMenuStatus: TemporaryConfigurationMenuStatusType;
  currentSelectedConfigurationType: string;
  updateSelectedConfigurationType: (id: string) => void;
};
function TemporaryNewSiteConfigurations(
  props: TemporaryNewSiteConfigurationsProps
) {
  const {
    siteConfiguration,
    temporaryConfigurationMenuStatus,
    currentSelectedConfigurationType,
    updateSelectedConfigurationType
  } = props;

  const { configurationTypeId, subConfigurations } = siteConfiguration;
  const {
    isOpen: isCurrentSectionOpen,
    hasVisitedConfigurationAtLeastOnce,
    isValid: isConfigurationValid,
    configurationErrorMessage,
    configurationDetails,
    numberOfErrors = 0
  } = temporaryConfigurationMenuStatus?.[configurationTypeId] || {};

  return (
    <MainContainer>
      <Stack
        flexDirection="column"
        key={`main-site-section-${configurationTypeId}`}
      >
        <Stack
          flexDirection="row"
          gap={1}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack
            flexDirection="row"
            gap={0.5}
            alignItems={"center"}
            sx={{
              width: "-webkit-fill-available"
            }}
          >
            {subConfigurations.length > 0 && (
              <IconButton
                aria-label="expand child section button"
                aria-expanded
                sx={{
                  width: "22px",
                  height: "22px",
                  py: 0.5,
                  px: 0.5
                }}
              >
                <ExpandMore />
              </IconButton>
            )}

            <ListItemButton
              onClick={() => {
                updateSelectedConfigurationType(configurationTypeId);
              }}
              selected={
                currentSelectedConfigurationType === configurationTypeId
              }
              aria-label="section button"
              aria-selected={
                currentSelectedConfigurationType === configurationTypeId
              }
              aria-expanded={
                isCurrentSectionOpen && subConfigurations.length > 0
              }
            >
              <ListItemText
                aria-label="section name"
                primary={configurationDetails.name}
              />
            </ListItemButton>
            {hasVisitedConfigurationAtLeastOnce &&
              !isConfigurationValid &&
              (numberOfErrors > 0 || configurationErrorMessage) && (
                <ErrorIcon
                  style={{
                    fill: mainColours.icon.error,
                    width: 20,
                    height: 20
                  }}
                  aria-label="configuration has errors icon"
                />
              )}
          </Stack>
          <Checkbox
            aria-label="is configuration ready checkbox"
            checked={isConfigurationValid && hasVisitedConfigurationAtLeastOnce}
            readOnly
            style={{
              height: 22,
              width: 22
            }}
          />
        </Stack>
        {subConfigurations.length > 0 && (
          <Box
            sx={{
              m: 0,
              p: 0,
              ...(isCurrentSectionOpen && {
                borderLeft: "1px solid",
                borderColor: (theme) => theme.palette.divider,
                pl: 0.4,
                ml: 1
              })
            }}
          >
            {subConfigurations.map((configuration: NewSiteConfiguration, i) => {
              return (
                <TemporaryNewSiteConfigurations
                  key={`site-section-${configuration.configurationTypeId}${i}`}
                  {...props}
                  siteConfiguration={configuration}
                />
              );
            })}
          </Box>
        )}
      </Stack>
    </MainContainer>
  );
}

export default TemporaryNewSiteConfigurations;
